import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  alpha,
  Box,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";
import AdminPageCardTitle from "components/AdminPageCardTitle/AdminPageCardTitle";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AdminTransactionData,
  AdminTransactionDetails,
} from "reduxStore/dataTypes";
import { adminTransactionDetailsRequest } from "services/apiService";
import theme, { responsiveFontSize } from "themes/Theme";
import { getStatusBgColor } from "utils/common";
import { formateTransactionDate } from "utils/dateFormat";
import { formatNumber } from "utils/formatter";
import LinkIcon from "@mui/icons-material/Link";
import ReceivedChartIcon from "assets/svg/ReceivedChartIcon";
import SendChartIcon from "assets/svg/SendChartIcon";

const AdminTransactionDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [txnData, setTxnData] = useState<AdminTransactionData | null>(null);

  const getTransactionDetails = async () => {
    try {
      if (!id) return;
      const response: AxiosResponse<AdminTransactionDetails> =
        await adminTransactionDetailsRequest(id);
      if (response && response.status === 200) {
        setTxnData(response?.data?.data);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  useEffect(() => {
    if (id) {
      getTransactionDetails();
    }
  }, [id]);

  const RenderField = ({ label, value, icon }: any) => {
    return (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={1}
        mt={1}
        spacing={{ xs: 0.5, sm: 1 }}
      >
        <Typography sx={styles.inputLabelStyle}>{label}</Typography>
        <Typography sx={styles.inputValueStyle}>{value}</Typography>
        {icon}
      </Stack>
    );
  };

  return (
    <Box sx={styles.container}>
      <AdminPageCardTitle
        title={"Transaction Details"}
        icon={<ArrowBackIcon sx={{ color: theme.palette.primary.main }} />}
        onIconClick={() => {
          navigate(-1);
        }}
      />
      <Box sx={{ mt: 3 }} />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={1}
        mt={3}
        spacing={{ xs: 0.5, sm: 1 }}
      >
        <Typography sx={styles.inputLabelStyle}>
          {"Transaction Hash"}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={1}
        >
          <Typography sx={styles.inputValueStyle}>
            {txnData?.txHash !== "-" ? txnData?.txHash : "-"}
          </Typography>
          {txnData?.txHash !== "-" ? (
            <IconButton>
              <LinkIcon
                sx={{ color: theme.palette.primary.main }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    `${process.env.REACT_APP_EXPLORE_URL}/tx/${txnData?.txHash}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
              />
            </IconButton>
          ) : null}
        </Stack>
      </Stack>
      <Divider
        sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
      />
      <RenderField
        label={"User Name"}
        value={`${txnData?.firstName} ${txnData?.lastName}`}
      />
      <Divider
        sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
      />
      <RenderField
        label={"Type"}
        value={txnData?.txType}
        icon={
          txnData?.txType?.toLowerCase() === "receive" ? (
            <SendChartIcon color="green" width={14} height={14} />
          ) : (
            <ReceivedChartIcon color="red" width={14} height={14} />
          )
        }
      />
      <Divider
        sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
      />
      <RenderField
        label={"Amount"}
        value={`$${
          txnData?.amount ? formatNumber(Number(txnData?.amount)) : "-"
        } ${txnData?.cryptoTicker}`}
      />
      <Divider
        sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
      />
      <RenderField
        label={"From Address"}
        value={`${txnData?.fromAddress ? txnData?.fromAddress : "-"} `}
      />
      <Divider
        sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
      />
      <RenderField
        label={"To Address"}
        value={`${txnData?.toAddress ? txnData?.toAddress : "-"} `}
      />
      <Divider
        sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
      />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={1}
        mt={1}
        spacing={{ xs: 0.5, sm: 1 }}
      >
        <Typography sx={styles.inputLabelStyle}>{"Status"}</Typography>
        <Box
          sx={{
            backgroundColor: alpha(
              getStatusBgColor(txnData?.status?.toLowerCase()),
              0.1
            ),
            borderRadius: 2,
            px: 2,
            py: 0.5,
          }}
        >
          <Typography
            sx={{
              ...styles.inputValueStyle,
              color: getStatusBgColor(txnData?.status?.toLowerCase()),
            }}
          >
            {txnData?.status}
          </Typography>
        </Box>
      </Stack>
      <Divider
        sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
      />
      {txnData?.statusReason ? (
        <>
          <RenderField
            label={"Status Reason"}
            value={txnData?.statusReason ? txnData?.statusReason : "-"}
          />
          <Divider
            sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
          />
        </>
      ) : null}
      <RenderField
        label={"Creation Date"}
        value={
          txnData?.createdAt ? formateTransactionDate(txnData?.createdAt) : "-"
        }
      />
    </Box>
  );
};
export default AdminTransactionDetailsPage;
const styles = {
  container: {
    flexGrow: 1,
    borderRadius: 3,
    boxShadow: `rgba(8, 60, 61, 0.27) 0px 1px 5px 0px, rgba(8, 60, 61, 0.06) 0px 1px 5px 0px`,
    backgroundColor: "rgb(255, 255, 255)",
    p: { xs: 1, sm: 2, md: 3 },
    ml: { xs: 0, md: -5 },
    width: "100%",
    minWidth: 0,
    maxWidth: "100vw",
  },
  inputLabelStyle: {
    fontSize: { xs: responsiveFontSize(12), sm: responsiveFontSize(14) },
    fontWeight: 400,
    color: theme.palette.text.primary,
    width: { xs: "100%", sm: "15em" },
    wordBreak: "break-word",
  },
  inputValueStyle: {
    fontSize: { xs: responsiveFontSize(12), sm: responsiveFontSize(14) },
    fontWeight: 400,
    color: theme.palette.primary.main,
    wordBreak: "break-word",
  },
};
